import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Upload, message, Image, Space, Popconfirm, Select, InputNumber 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, PictureOutlined } from '@ant-design/icons';
import API_ENDPOINTS, { getProductApiUrl } from '../config/api';

const { TextArea } = Input;
const { Option } = Select;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImagesModalVisible, setIsImagesModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [unitTypes, setUnitTypes] = useState([
    { value: 'pcs', label: 'Pieces' },
    { value: 'boxes', label: 'Boxes' },
    { value: 'cartons', label: 'Cartons' },
    { value: 'bottles', label: 'Bottles' }
  ]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PRODUCTS);
      const result = await response.json();
      if (result.status === 'success') {
        setProducts(result.data);
      }
    } catch (error) {
      message.error('Failed to fetch products');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CATEGORIES);
      const result = await response.json();
      if (result.status === 'success') {
        setCategories(result.data);
      }
    } catch (error) {
      message.error('Failed to fetch categories');
    }
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    setImageUrl('');
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    setImageUrl(record.image_url);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      price: parseFloat(record.price),
      category_id: record.category_id,
      unit_type: record.unit_type ? record.unit_type.split(',').map(unit => unit.trim()) : []
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.PRODUCTS}/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.status === 'success') {
        message.success('Product deleted successfully');
        fetchProducts();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to delete product');
    }
  };

  const handleManageImages = async (record) => {
    try {
      setProductImages([]);
      const response = await fetch(`${API_ENDPOINTS.PRODUCT_IMAGES}/${record.id}/images`);
      const result = await response.json();
      if (result.status === 'success') {
        const filteredImages = Array.isArray(result.data) 
          ? result.data.filter(image => image.product_id === record.id)
          : [];
        setProductImages(filteredImages);
        setEditingProduct(record);
        setIsImagesModalVisible(true);
      } else {
        message.error(result.message || 'Failed to fetch product images');
      }
    } catch (error) {
      console.error('Error fetching product images:', error);
      setProductImages([]);
      message.error('Failed to fetch product images');
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('product_id', editingProduct.id);

      const response = await fetch(`${API_ENDPOINTS.PRODUCT_IMAGES}/${editingProduct.id}/images`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (result.status === 'success') {
        message.success('Image uploaded successfully');
        // Refresh product images
        handleManageImages(editingProduct);
      } else {
        message.error(result.message || 'Failed to upload image');
      }
    } catch (error) {
      message.error('Failed to upload image');
    }
  };

  const handleImageDelete = async (imageId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.PRODUCT_IMAGES}/${editingProduct.id}/images/${imageId}`, {
        method: 'DELETE',
      });

      const result = await response.json();
      if (result.status === 'success') {
        message.success('Image deleted successfully');
        // Refresh product images
        handleManageImages(editingProduct);
      } else {
        message.error(result.message || 'Failed to delete image');
      }
    } catch (error) {
      message.error('Failed to delete image');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Append basic product data
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('price', values.price.toString());
      formData.append('category_id', values.category_id.toString());
      formData.append('unit_type', (values.unit_type || []).join(','));

      // Handle image upload
      if (values.image && values.image[0]) {
        const file = values.image[0].originFileObj;
        console.log('Uploading file:', file);
        formData.append('image', file);
      }

      let url = API_ENDPOINTS.PRODUCTS;
      let method = 'POST';

      // If editing, adjust the URL and method
      if (editingProduct) {
        url = `${API_ENDPOINTS.PRODUCTS}/${editingProduct.id}`;
        method = 'POST';
        formData.append('_method', 'PUT');
        formData.append('id', editingProduct.id.toString());
      }

      console.log('Submitting to URL:', url);
      console.log('Method:', method);
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ':', pair[1] instanceof File ? pair[1].name : pair[1]);
      }

      const response = await fetch(url, {
        method: method,
        body: formData,
      });

      const result = await response.json();
      console.log('Server response:', result);
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to save product');
      }
      
      if (result.status === 'success') {
        message.success(editingProduct ? 'Product updated successfully' : 'Product created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setImageUrl('');
        fetchProducts();
      } else {
        throw new Error(result.message || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      message.error(error.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleUnitTypeChange = (value, option) => {
    // If the value is not in our existing options, add it
    const newValues = value.filter(v => !unitTypes.some(type => type.value === v));
    if (newValues.length > 0) {
      const newOptions = newValues.map(v => ({ value: v, label: v }));
      setUnitTypes([...unitTypes, ...newOptions]);
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image',
      render: (image_url) => (
        <Image
          src={image_url}
          alt="Product"
          width={50}
          height={50}
          style={{ objectFit: 'cover' }}          
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
    },
    {
      title: 'Unit Types',
      dataIndex: 'unit_type',
      key: 'unit_type',
      render: (unit_type) => unit_type ? unit_type.split(',').join(', ') : '-',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `$${parseFloat(price).toFixed(2)}`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<PictureOutlined />} 
            onClick={() => handleManageImages(record)}
          >
            Manage Images
          </Button>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <h1>Products</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
          Add New Product
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={products}
        rowKey="id"
      />

      <Modal
        title={editingProduct ? 'Edit Product' : 'Add New Product'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: 'Please input product name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="category_id"
            label="Category"
            rules={[{ required: true, message: 'Please select a category!' }]}
          >
            <Select placeholder="Select a category">
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="unit_type"
            label="Unit Types"
            rules={[{ required: true, message: 'Please select at least one unit type!' }]}
            extra="You can type to add new unit types"
          >
            <Select
              mode="tags"
              placeholder="Select or add unit types"
              options={unitTypes}
              onChange={handleUnitTypeChange}
              style={{ width: '100%' }}
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Form.Item
            name="price"
            label="Price"
            rules={[{ required: true, message: 'Please input price!' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              prefix="$"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="image"
            label="Product Image"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
              onChange={({ fileList }) => {
                if (fileList.length > 0) {
                  console.log('Selected file:', fileList[0]);
                }
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="product" style={{ width: '100%' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingProduct ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Manage Images - ${editingProduct?.name || ''}`}
        open={isImagesModalVisible}
        onCancel={() => setIsImagesModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Upload
            accept="image/*"
            beforeUpload={(file) => {
              handleImageUpload(file);
              return false;
            }}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Upload New Image</Button>
          </Upload>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
          {productImages.map((image) => (
            <div key={image.id} style={{ position: 'relative' }}>
              <Image
                src={image.image_url}
                alt="Product"
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
              />
              <div style={{ position: 'absolute', top: 8, right: 8 }}>
                <Popconfirm
                  title="Are you sure you want to delete this image?"
                  onConfirm={() => handleImageDelete(image.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Products; 